import { useSyncExternalStore } from "react";

export type ThemePreference = "light" | "dark" | "system";
const storageKey = "scribe10-theme";
const listeners = new Set<() => void>();
let preference: ThemePreference = "system";
let initialized = false;
let media: MediaQueryList | undefined;

function readPreference(): ThemePreference {
  try {
    const saved = localStorage.getItem(storageKey);
    return saved === "light" || saved === "dark" ? saved : "system";
  } catch { return "system"; }
}

function applyTheme() {
  const resolved = preference === "system" ? (media?.matches ? "dark" : "light") : preference;
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.classList.toggle("dark", resolved === "dark");
  listeners.forEach((listener) => listener());
}

export function initializeTheme() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  preference = readPreference();
  media = window.matchMedia("(prefers-color-scheme: dark)");
  media.addEventListener("change", () => { if (preference === "system") applyTheme(); });
  window.addEventListener("storage", (event) => {
    if (event.key === storageKey) { preference = readPreference(); applyTheme(); }
  });
  applyTheme();
}

export function setThemePreference(value: ThemePreference) {
  preference = value;
  try { localStorage.setItem(storageKey, value); } catch { /* Still works for this tab. */ }
  applyTheme();
}

export function useThemePreference() {
  return useSyncExternalStore(
    (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
    () => preference,
    () => "system" as ThemePreference,
  );
}

export function ThemeSwitcher() {
  const theme = useThemePreference();
  return <label className="theme-switcher">
    <span>Theme</span>
    <select aria-label="Color theme" value={theme} onChange={(event) => setThemePreference(event.target.value as ThemePreference)}>
      <option value="light">Light</option>
      <option value="dark">Dark</option>
      <option value="system">System</option>
    </select>
  </label>;
}
